import { baseApi, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { onecEnvelopeDecoder, type OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import type { CreateDefaults } from '../model/types'
import { decodeCreateDefaults } from './contracts'
import { ONEC_CREATE_DEFAULTS_URLS } from './urls'

/**
 * «Создание по умолчанию» базы 1С в ОБЩЕМ `baseApi` (RP-SYN11). Справочники
 * для выбора служба читает из 1С на лету; в браузер ответ не сохраняется —
 * живёт только в кэше запроса. Сохранение заменяет настройки целиком, его
 * ответ становится ответом чтения.
 */
export const ONEC_CREATE_DEFAULTS_TAG = 'OneCCreateDefaults' as const

export interface CreateDefaultsArgs {
  resourceId: string
}

export interface SaveCreateDefaultsArgs extends CreateDefaultsArgs {
  /** Ключ поля → значение; `null` — умолчания нет. */
  values: Readonly<Record<string, string | null>>
}

export const createDefaultsTag = (resourceId: string) => resourceTag(ONEC_CREATE_DEFAULTS_TAG, normalizeRid(resourceId), 'CREATE-DEFAULTS')

export const createDefaultsApi = baseApi.enhanceEndpoints({ addTagTypes: [ONEC_CREATE_DEFAULTS_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    onecCreateDefaults: build.query<OneCEnvelope<CreateDefaults>, CreateDefaultsArgs>({
      query: ({ resourceId }) => ({
        url: ONEC_CREATE_DEFAULTS_URLS.defaults(resourceId),
        // Чтение идёт в 1С: отказ показывается человеку сразу, следующий запрос — его «Повторить».
        noRetry: true,
        decode: onecEnvelopeDecoder(resourceId, decodeCreateDefaults),
      }),
      providesTags: (_result, _error, { resourceId }) => [createDefaultsTag(resourceId), resourceScopeTag(normalizeRid(resourceId))],
    }),
    /** Сохранить настройки целиком. Записи в 1С нет: значения проверяются по справочникам базы и хранятся у службы. */
    onecCreateDefaultsSave: build.mutation<OneCEnvelope<CreateDefaults>, SaveCreateDefaultsArgs>({
      query: ({ resourceId, values }) => ({
        url: ONEC_CREATE_DEFAULTS_URLS.defaults(resourceId),
        method: 'PUT',
        body: { values },
        decode: onecEnvelopeDecoder(resourceId, decodeCreateDefaults),
      }),
      async onQueryStarted({ resourceId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          void dispatch(createDefaultsApi.util.upsertQueryData('onecCreateDefaults', { resourceId }, data))
        } catch {
          // Отказ показывает сам результат мутации; кэш чтения не трогаем.
        }
      },
    }),
  }),
})

export const { onecCreateDefaults, onecCreateDefaultsSave } = createDefaultsApi.endpoints

/** Перечитать настройки и справочники базы («Повторить» после отказа 1С). */
export const reloadCreateDefaults = (resourceId: string) => createDefaultsApi.util.invalidateTags([createDefaultsTag(resourceId)])
